import { Route, Redirect } from 'react-router-dom'; // React Router 5
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';
import DashboardLayout from '../../layouts/DashboardLayout';
import PublicLayout from '../../layouts/PublicLayout';

/**
 * PrivateRoute - Route wrapper for protected pages with automatic layout wrapping
 * 
 * Phase 5: Refactored to handle layout automatically from route configuration
 * 
 * Layout types:
 * - 'dashboard': Wraps with DashboardLayout
 * - 'public': Wraps with PublicLayout
 * - 'none' or undefined: No layout wrapper
 * 
 * CRITICAL: Uses React Router 5 syntax (<Route render={...} />)
 * 
 * @param {Component} component - Protected component to render
 * @param {string} layout - Layout type ('dashboard', 'public', 'none')
 * @param {Object} rest - Other Route props (path, exact, etc.)
 */
const PrivateRoute = ({ component: Component, layout, ...rest }) => {
  const { isAuthenticated, loading } = useSelector((state) => state.auth);

  // Get layout component based on layout type
  const getLayoutComponent = () => {
    switch (layout) {
      case 'dashboard':
        return DashboardLayout;
      case 'public':
        return PublicLayout;
      case 'none':
      default:
        return null;
    }
  };

  const Layout = getLayoutComponent();

  return (
    <Route
      {...rest}
      render={(props) => {
        // Loading → show spinner (App.jsx is initializing auth)
        if (loading) {
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                gap: 2
              }}
            >
              <CircularProgress size={60} />
              <Typography variant="body1" color="text.secondary">
                Yükleniyor...
              </Typography>
            </Box>
          );
        }

        // Not authenticated → redirect to login
        if (!isAuthenticated) {
          return <Redirect to="/login" />;
        }

        // Authenticated → render component with layout if specified
        const ComponentToRender = <Component {...props} />;
        
        if (Layout) {
          return <Layout>{ComponentToRender}</Layout>;
        }
        
        return ComponentToRender;
      }}
    />
  );
};

PrivateRoute.propTypes = {
  component: PropTypes.elementType.isRequired
};

export default PrivateRoute;

